import React, { useState, useEffect } from 'react';
import { Plus, Search, Trash2, LayoutGrid, List, Calendar, MapPin, Users, CreditCard as Edit, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useEventsStore } from '../stores/eventsStore';
import { useBeneficiairesStore } from '../stores/beneficiairesStore';
import AtelierForm from '../components/events/AtelierForm';
import { auth } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { useThemeStore } from '../stores/themeStore';
import { checkAdminStatus } from '../utils/checkAdminStatus';
import { ATELIER_TYPES, getAtelierTypeLabel, getAtelierTypeDescription } from '../data/atelierTypes';

export default function Ateliers() {
  const navigate = useNavigate();
  const { events, loadEvents, addEvent, deleteEvent, updateEvent, deleteAllEvents } = useEventsStore();
  const { beneficiaires } = useBeneficiairesStore();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [selectedAteliers, setSelectedAteliers] = useState<Set<string>>(new Set());
  const [editingAtelier, setEditingAtelier] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const { darkMode } = useThemeStore();
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState<string>('all');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async user => {
      if (!user) {
        navigate('/login');
        return;
      }
      loadEvents();
      const admin = await checkAdminStatus(user.email || '');
      setIsAdmin(admin);
    });
    return () => unsubscribe();
  }, [navigate, loadEvents]);

  const getBeneficiairesCountByUser = (userId: string) => {
    return beneficiaires.filter(b => b.userId === userId).length;
  };

  const ateliers = events.filter(e => e.type === 'workshop');

  const filteredAteliers = ateliers.filter(atelier => {
    let matchesSearch = true;
    if (searchTerm) {
      matchesSearch = atelier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (atelier.workshopType || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        atelier.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getAtelierTypeLabel(atelier.workshopType || '').toLowerCase().includes(searchTerm.toLowerCase());
    }
    let matchesType = true;
    if (filterType !== 'all' && atelier.workshopType !== filterType) {
      matchesType = false;
    }
    return matchesSearch && matchesType;
  });

  const totalPages = Math.ceil(filteredAteliers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAteliers = filteredAteliers.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleFormSubmit = async (data: any) => {
    try {
      if (editingAtelier) {
        await updateEvent(editingAtelier.id, data);
        setEditingAtelier(null);
        toast.success('Atelier mis à jour avec succès');
      } else {
        await addEvent({
          ...data,
          type: 'workshop',
          partners: [],
          status: 'upcoming'
        });
        toast.success('Atelier créé avec succès');
      }
      setShowNewForm(false);
    } catch (error) {
      console.error('Error saving atelier:', error);
      toast.error("Erreur lors de l'enregistrement de l'atelier");
    }
  };

  const handleEditAtelier = (atelier: any) => {
    setEditingAtelier(atelier);
    setShowNewForm(true);
  };

  const handleDeleteSelected = async () => {
    if (selectedAteliers.size === 0) return;

    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedAteliers.size} atelier${selectedAteliers.size > 1 ? 's' : ''} ?`)) {
      try {
        for (const id of selectedAteliers) {
          await deleteEvent(id);
        }
        setSelectedAteliers(new Set());
        toast.success(`${selectedAteliers.size} atelier${selectedAteliers.size > 1 ? 's' : ''} supprimé${selectedAteliers.size > 1 ? 's' : ''} avec succès`);
      } catch (error) {
        console.error('Error deleting ateliers:', error);
        toast.error('Erreur lors de la suppression des ateliers');
      }
    }
  };

  const toggleAtelier = (id: string) => {
    setSelectedAteliers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Ateliers</h1>
        <div className="flex gap-4">
          <button
            onClick={() => {
              setEditingAtelier(null);
              setShowNewForm(true);
            }}
            className="flex items-center px-4 py-2 bg-gradient-anthea text-white rounded-2xl hover:opacity-90 transition-opacity"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nouvel atelier
          </button>
          {isAdmin && (
            <button
              onClick={async () => {
                if (ateliers.length === 0) {
                  toast.error('Aucun atelier à supprimer');
                  return;
                }
                if (window.confirm(`Êtes-vous sûr de vouloir supprimer tous les ${ateliers.length} ateliers ? Cette action est irréversible.`)) {
                  try {
                    const ids = ateliers.map(a => a.id);
                    for (const id of ids) {
                      await deleteEvent(id);
                    }
                    setSelectedAteliers(new Set());
                  } catch (error) {
                    console.error('Error deleting all ateliers:', error);
                  }
                }
              }}
              disabled={ateliers.length === 0}
              className={`flex items-center px-4 py-2 text-sm ${darkMode ? 'bg-red-900 text-red-300' : 'bg-red-50 text-red-600'} rounded-2xl hover:${darkMode ? 'bg-red-800' : 'bg-red-100'} disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer tout
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Rechercher un atelier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-anthea-blue ${darkMode ? 'bg-gray-700 text-white border-gray-600' : ''}`}
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className={`px-4 py-2 border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200'} rounded-2xl focus:outline-none focus:ring-2 focus:ring-anthea-blue`}
        >
          <option value="all">Tous les types</option>
          {ATELIER_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <div className={`flex gap-2 border ${darkMode ? 'border-gray-600' : 'border-gray-200'} rounded-2xl p-1`}>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-xl ${viewMode === 'grid' ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : (darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50')}`}
          >
            <LayoutGrid className={`w-5 h-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-xl ${viewMode === 'list' ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : (darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50')}`}
          >
            <List className={`w-5 h-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
          </button>
        </div>
      </div>

      {selectedAteliers.size > 0 && (
        <div className={`flex justify-between items-center p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl`}>
          <div className="flex items-center gap-4">
            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {selectedAteliers.size} atelier{selectedAteliers.size > 1 ? 's' : ''} sélectionné{selectedAteliers.size > 1 ? 's' : ''}
            </span>
          </div>
          <button
            onClick={handleDeleteSelected}
            className={`flex items-center px-3 py-1.5 text-sm ${darkMode ? 'bg-red-900 text-red-300' : 'bg-red-50 text-red-600'} rounded-lg hover:${darkMode ? 'bg-red-800' : 'bg-red-100'}`}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Supprimer la sélection
          </button>
        </div>
      )}

      {filteredAteliers.length === 0 ? (
        <div className={`text-center py-16 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <p className="text-lg">Aucun atelier trouvé</p>
          <p className="text-sm mt-2">Créez votre premier atelier pour commencer l'accompagnement de vos candidats.</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedAteliers.map((atelier) => (
            <div
              key={atelier.id}
              className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer relative`}
            >
              <div className="absolute top-4 left-4 z-10">
                <input
                  type="checkbox"
                  checked={selectedAteliers.has(atelier.id)}
                  onChange={() => toggleAtelier(atelier.id)}
                  className={`h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 ${darkMode ? 'bg-gray-600 border-gray-500' : ''}`}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="absolute top-4 right-4 z-10">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditAtelier(atelier);
                  }}
                  className={`p-1.5 ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-50 text-blue-600'} rounded-lg hover:${darkMode ? 'bg-blue-800' : 'bg-blue-100'} transition-colors`}
                  title="Modifier l'atelier"
                >
                  <Edit className="w-4 h-4" />
                </button>
              </div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4 mt-6">
                  <div>
                    <h3 className={`font-semibold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{atelier.name}</h3>
                    <p className={`text-sm font-medium ${darkMode ? 'text-anthea-lime' : 'text-anthea-blue'}`}>
                      {getAtelierTypeLabel(atelier.workshopType || 'cv')}
                    </p>
                    {atelier.workshopType && (
                      <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {getAtelierTypeDescription(atelier.workshopType)}
                      </p>
                    )}
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    atelier.status === 'upcoming' ? (darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800') :
                    atelier.status === 'ongoing' ? (darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800') :
                    atelier.status === 'completed' ? (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800') :
                    (darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800')
                  }`}>
                    {atelier.status === 'upcoming' ? 'À venir' :
                     atelier.status === 'ongoing' ? 'En cours' :
                     atelier.status === 'completed' ? 'Terminé' :
                     'Annulé'}
                  </span>
                </div>

                <div className={`space-y-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    {new Date(atelier.date).toLocaleDateString('fr-FR')} ({atelier.startTime} - {atelier.endTime})
                  </div>
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-2" />
                    {atelier.location}
                  </div>
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-2" />
                    {atelier.invitedBeneficiaires.length} invité{atelier.invitedBeneficiaires.length > 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
          {paginatedAteliers.map((atelier) => (
            <div
              key={atelier.id}
              className={`flex items-center p-4 hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'} transition-colors cursor-pointer`}
            >
              <input
                type="checkbox"
                checked={selectedAteliers.has(atelier.id)}
                onChange={() => toggleAtelier(atelier.id)}
                className={`h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mr-4 ${darkMode ? 'bg-gray-600 border-gray-500' : ''}`}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{atelier.name}</h3>
                    <p className={`text-sm font-medium ${darkMode ? 'text-anthea-lime' : 'text-anthea-blue'}`}>
                      {getAtelierTypeLabel(atelier.workshopType || 'cv')}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditAtelier(atelier);
                      }}
                      className={`p-1.5 ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-50 text-blue-600'} rounded-lg hover:${darkMode ? 'bg-blue-800' : 'bg-blue-100'} transition-colors`}
                      title="Modifier l'atelier"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      atelier.status === 'upcoming' ? (darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800') :
                      atelier.status === 'ongoing' ? (darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800') :
                      atelier.status === 'completed' ? (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800') :
                      (darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800')
                    }`}>
                      {atelier.status === 'upcoming' ? 'À venir' :
                       atelier.status === 'ongoing' ? 'En cours' :
                       atelier.status === 'completed' ? 'Terminé' :
                       'Annulé'}
                    </span>
                  </div>
                </div>
                <div className={`mt-2 grid grid-cols-3 gap-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    {new Date(atelier.date).toLocaleDateString('fr-FR')}
                  </div>
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-2" />
                    {atelier.location}
                  </div>
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-2" />
                    {atelier.invitedBeneficiaires.length} invité{atelier.invitedBeneficiaires.length > 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination controls */}
      <div className="flex flex-col items-center mt-6 mb-16 gap-4">
        <div className="flex items-center gap-2">
          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Afficher</span>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className={`border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200'} rounded-lg p-1 text-sm`}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>ateliers par page</span>
        </div>

        <div className="flex items-center gap-2">
          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {filteredAteliers.length > 0 ? `${startIndex + 1}-${Math.min(endIndex, filteredAteliers.length)} sur ${filteredAteliers.length}` : '0-0 sur 0'}
          </span>

          <div className="flex gap-1">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || filteredAteliers.length === 0}
              className={`p-2 border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'} rounded-lg disabled:opacity-50`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageToShow;
              if (totalPages <= 5) {
                pageToShow = i + 1;
              } else if (currentPage <= 3) {
                pageToShow = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageToShow = totalPages - 4 + i;
              } else {
                pageToShow = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageToShow}
                  onClick={() => handlePageChange(pageToShow)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                    currentPage === pageToShow
                      ? 'bg-gradient-anthea text-white'
                      : `border ${darkMode ? 'border-gray-600 hover:bg-gray-700 text-gray-300' : 'border-gray-200 hover:bg-gray-50'}`
                  }`}
                >
                  {pageToShow}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || filteredAteliers.length === 0}
              className={`p-2 border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'} rounded-lg disabled:opacity-50`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {showNewForm && (
        <AtelierForm
          onSubmit={handleFormSubmit}
          onClose={() => {
            setShowNewForm(false);
            setEditingAtelier(null);
          }}
          atelier={editingAtelier}
          isEditing={!!editingAtelier}
        />
      )}
    </div>
  );
}
